import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    query, where, orderBy, limit,
} from 'firebase/firestore';
import { RenderIf } from 'react-rainbow-components';
import ButtonIcon from 'components/ButtonIcon';
import InstagramSolid from 'components/icons/instagramSolid';
import ChatIcon from 'components/icons/chat';
import { EntityGet } from 'data/firestore/types';
import { Customer } from 'data/firestore/agent/customer/types';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import PhoneIcon from 'components/icons/phone';
import SmsIcon from 'components/icons/sms';
import FacebookSolid from 'components/icons/facebookSolid';
import TiktokSolid from 'components/icons/tiktokSolid';
import useThreads from 'data/firestore/agent/thread/useCollection';
import useSMSChat from 'hooks/useSMSChat';
import useCustomerPanel from 'hooks/useCustomerPanel';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import { useOpenModal } from '@rainbow-modules/hooks';
import useAgentData from 'hooks/useAgentData';
import { useNexhealthPatientLink } from 'customApps/nexhealth/hooks';
import {
    AvatarFrame,
    BasicInfoContainer,
    CustomerAvatar,
    CustomerName,
    NexhealthMark,
    StyledLink,
    LeftContent,
    RightContent,
    StyledBadgeOverlay,
    CustomerButton,
} from './styled';
import { CUSTOMER_CALL_MODAL } from '../../../constants';

const CustomerBasicInfo = ({
    isLoading = false,
    data,
}: {
    isLoading?: boolean;
    data?: EntityGet<Customer>;
}) => {
    const { agentId = '' } = useParams<{ agentId: string }>();
    const { isAppEnabled } = useAgentData();
    const { data: linkResult } = useNexhealthPatientLink(
        agentId,
        data?.id || '',
        isAppEnabled('nexhealth') && Boolean(data?.id),
    );
    const [link] = linkResult?.data || [];
    const isNexhealthLinked = Boolean(link && !link.stale);
    const navigate = useNavigateWithQuery();

    const customerDisplayName = useMemo(
        () => getDisplayName(data, formatIntlPhoneNumber(data?.phoneNumber)),
        [data],
    );
    const { openChat } = useSMSChat();
    const handleSMSClick = useCallback(
        () => openChat({ ...data }),
        [openChat, data],
    );

    const { openCustomerPanel } = useCustomerPanel();
    const handleCustomerPanelClick = useCallback(
        () => openCustomerPanel({ data }),
        [openCustomerPanel, data],
    );

    const [openCustomerCallModal] = useOpenModal(CUSTOMER_CALL_MODAL);

    const { data: threads = [] } = useThreads(agentId, {
        listQueryFn: (ref) => query(
            ref,
            where('customer.id', '==', data?.id || ''),
            orderBy('lastMessage.createdAt', 'desc'),
            limit(1),
        ),
        disabled: !agentId || !data?.id,
        track: [agentId, data?.id],
    });

    const [latestThread] = threads || [];

    const handleThreadClick = useCallback(
        () => {
            if (latestThread?.id) {
                navigate(`/${agentId}/threads/${latestThread.id}`);
            }
        },
        [latestThread, agentId, navigate],
    );

    if (isLoading) return null;

    return (
        <BasicInfoContainer>
            <LeftContent>
                <CustomerButton type="button" onClick={handleCustomerPanelClick}>
                    <AvatarFrame>
                        <CustomerAvatar
                            size="large"
                            initials={getInitials(data as unknown as Record<string, unknown>)}
                            src={data?.photoURL}
                            value={data?.id}
                        />
                        <RenderIf isTrue={isNexhealthLinked}>
                            <NexhealthMark title="Linked to NexHealth" />
                        </RenderIf>
                    </AvatarFrame>
                    <CustomerName>{customerDisplayName}</CustomerName>
                </CustomerButton>
            </LeftContent>
            <RightContent>
                <RenderIf isTrue={data?.phoneNumber}>
                    <ButtonIcon
                        icon={<PhoneIcon />}
                        onClick={() => openCustomerCallModal({ customer: data })}
                    />
                </RenderIf>
                <RenderIf isTrue={data?.phoneNumber}>
                    <ButtonIcon
                        icon={(
                            <StyledBadgeOverlay
                                overlap="circle"
                                isHidden={!data?.hasNewMessage}
                            >
                                <SmsIcon />
                            </StyledBadgeOverlay>
                        )}
                        onClick={handleSMSClick}
                    />
                </RenderIf>
                <RenderIf isTrue={data?.instagramProfileURL}>
                    <StyledLink to={String(data?.instagramProfileURL)} target="_blank">
                        <InstagramSolid />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={data?.facebookProfileURL}>
                    <StyledLink to={String(data?.facebookProfileURL)} target="_blank">
                        <FacebookSolid />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={data?.tiktokProfileURL}>
                    <StyledLink to={String(data?.tiktokProfileURL)} target="_blank">
                        <TiktokSolid />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={!!latestThread}>
                    <ButtonIcon
                        icon={<ChatIcon />}
                        onClick={handleThreadClick}
                    />
                </RenderIf>
            </RightContent>
        </BasicInfoContainer>
    );
};

export default CustomerBasicInfo;
